import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiPackage,
  FiRefreshCw,
  FiRepeat,
  FiRotateCcw,
  FiSearch,
  FiSend,
  FiSlash,
  FiTrash2,
  FiUser,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, CustomPopup, DeleteConfirm, PageHeader } from '../components/common/index.js';
import { returnsApi } from '../api/returnsApi';

const TABS = {
  NEW: 'new',
  LOG: 'log',
};

const RETURN_TYPES = {
  EXCHANGE: 'exchange',
  CASH: 'cash',
};

// Same delivery-ETA presets the supervisor app offers for OSM parts — an
// exchange replacement can take just as long to source as the original part.
const EXCHANGE_ETA_OPTIONS = ['Ready Stock', '3 to 5 Days', '7 to 10 Days'];

// Cash payout window admin picks when logging the return — 0 to 10 days.
const PAYOUT_DAY_OPTIONS = Array.from({ length: 11 }, (_, n) => ({
  value: String(n),
  label: `${n} day${n === 1 ? '' : 's'}`,
}));

const STATUS_META = {
  pending:   { label: 'Pending',   className: 'bg-amber-50 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:  { label: 'Rejected',  className: 'bg-rose-50 text-rose-600 border-rose-200' },
};

const formatPrice = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const shortDate = (value) => (value ? String(value).split(' at ')[0] : '-');

const errorMessage = (error, fallback) => {
  const data = error?.response?.data ?? error;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail || data.message || data.error) return data.detail || data.message || data.error;
  return fallback;
};

const Modal = ({ title, subtitle, children, onClose }) => {
  useEffect(() => {
    const onEscape = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close dialog" />
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-fade-in-up">
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <svg className="h-8 w-8 animate-spin text-violet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  </div>
);

const StatCard = ({ icon: Icon, label, value, tone = 'violet' }) => {
  const tones = {
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mt-0.5 text-2xl font-bold leading-none text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ active, icon: Icon, label, count, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-bold transition-all ${
      active ? 'bg-violet-600 text-white shadow-sm shadow-violet-200' : 'text-slate-500 hover:bg-white hover:text-slate-800'
    }`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="truncate">{label}</span>
    {count != null && (
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>
        {count}
      </span>
    )}
  </button>
);

// From→to date range with a quick "Today" and clear. `label` says what the
// dates mean (sold date vs. return date).
const DateRange = ({ from, to, onFrom, onTo, onClear, label }) => {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <FiCalendar className="h-4 w-4 shrink-0 text-slate-400" title={label} />
        <input type="date" value={from} max={to || undefined} onChange={(e) => onFrom(e.target.value)} className="bg-transparent text-sm text-slate-700 outline-none" aria-label={`${label} from`} />
        <span className="text-slate-300">→</span>
        <input type="date" value={to} min={from || undefined} onChange={(e) => onTo(e.target.value)} className="bg-transparent text-sm text-slate-700 outline-none" aria-label={`${label} to`} />
      </div>
      <button
        type="button"
        onClick={() => { onFrom(today); onTo(today); }}
        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 shadow-sm transition hover:bg-slate-50"
      >
        Today
      </button>
      {(from || to) && (
        <button type="button" onClick={onClear} className="rounded-lg px-2 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100">
          Clear
        </button>
      )}
    </div>
  );
};

// ── Mark-return modal ─────────────────────────────────────────────────────────
// Cash or exchange can both be logged at any time — a fault may only show up
// weeks after the sale, so there is no sale-date cutoff.
const MarkReturnModal = ({ sale, part, submitting, error, onClose, onSubmit }) => {
  const [type, setType] = useState(RETURN_TYPES.CASH);
  const [exchangeFor, setExchangeFor] = useState('');
  const [exchangeEta, setExchangeEta] = useState('');
  const [refund, setRefund] = useState(part?.sold_price ? String(Number(part.sold_price)) : '');
  const [payoutDays, setPayoutDays] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState('');

  const submit = (event) => {
    event.preventDefault();
    setFormError('');

    const qty = parseInt(quantity, 10);
    if (!Number.isFinite(qty) || qty < 1) {
      setFormError('Quantity must be at least 1');
      return;
    }
    const payload = {
      inquiry_id: sale.inquiry_id,
      inquiry_part_id: part?.inquiry_part_id ?? undefined,
      product_name: part?.name ?? '',
      quantity: qty,
      return_type: type,
      reason: reason.trim(),
    };
    if (type === RETURN_TYPES.EXCHANGE) {
      if (!exchangeFor.trim()) {
        setFormError('Enter the product given in exchange');
        return;
      }
      if (!exchangeEta.trim()) {
        setFormError('Pick when the replacement will be ready');
        return;
      }
      payload.exchange_for = exchangeFor.trim();
      payload.exchange_eta = exchangeEta.trim();
    } else {
      const amount = Number(refund);
      if (!Number.isFinite(amount) || amount <= 0) {
        setFormError('Enter a valid refund amount');
        return;
      }
      if (payoutDays === '') {
        setFormError('Pick how many days admin has to pay this cash back');
        return;
      }
      payload.refund_amount = amount;
      payload.cash_payout_days = Number(payoutDays);
    }
    onSubmit(payload);
  };

  const inputBase =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-70';

  return (
    <Modal
      title="Mark product returned"
      subtitle={`${sale.customer_name || 'Customer'} · ${sale.car || ''}`}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert type="error" message={error} />}

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Product</p>
          <p className="mt-1 font-bold text-slate-900">{part?.name || 'Product'}</p>
          <p className="mt-1 text-sm text-slate-500">
            Sold {shortDate(sale.sold_at)}
            {part?.sold_price != null && ` · ${formatPrice(part.sold_price)}`}
            {part?.source ? ` · ${String(part.source).toUpperCase()}` : ''}
          </p>
        </div>

        {/* A cash refund on a part not yet handed over = a cancelled line. */}
        {type === RETURN_TYPES.CASH && part?.dispatched === false && (
          <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5">
            <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
            <p className="text-xs text-rose-700">
              This part hasn't been dispatched — a cash refund will mark it a{' '}
              <span className="font-bold">cancelled product</span>. It drops out of the order
              (never delivered) everywhere it shows.
            </p>
          </div>
        )}

        {/* Return type toggle — both allowed at any time */}
        <div>
          <p className="mb-2 block text-sm font-semibold text-slate-700">Return as</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: RETURN_TYPES.CASH, label: 'Cash refund', icon: FiDollarSign },
              { value: RETURN_TYPES.EXCHANGE, label: 'Exchange', icon: FiRepeat },
            ].map((opt) => {
              const active = type === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setType(opt.value)}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 text-sm font-bold transition-all ${
                    active
                      ? 'border-violet-500 bg-violet-50 text-violet-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'
                  }`}
                >
                  <opt.icon className="h-4 w-4" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Type-specific field */}
        {type === RETURN_TYPES.EXCHANGE ? (
          <div className="space-y-4">
            <div>
              <label htmlFor="exchange-for" className="mb-2 block text-sm font-semibold text-slate-700">
                Exchanged for *
              </label>
              <input
                id="exchange-for"
                value={exchangeFor}
                onChange={(e) => setExchangeFor(e.target.value)}
                disabled={submitting}
                placeholder="e.g. Side Mirror (Left), or a different part"
                className={inputBase}
              />
            </div>
            <div>
              <p className="mb-2 block text-sm font-semibold text-slate-700">Replacement ready in *</p>
              <div className="grid grid-cols-3 gap-2">
                {EXCHANGE_ETA_OPTIONS.map((opt) => {
                  const active = exchangeEta === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={submitting}
                      onClick={() => setExchangeEta(opt)}
                      className={`rounded-xl border-2 px-2 py-2.5 text-xs font-bold transition-all disabled:cursor-not-allowed disabled:opacity-70 ${
                        active
                          ? 'border-violet-500 bg-violet-50 text-violet-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="refund" className="mb-2 block text-sm font-semibold text-slate-700">
              Refund amount *
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input
                id="refund"
                type="number"
                min="0.01"
                step="any"
                inputMode="numeric"
                value={refund}
                onChange={(e) => setRefund(e.target.value)}
                disabled={submitting}
                placeholder="4000.00"
                className={`${inputBase} pl-9`}
              />
            </div>

            <div className="mt-4">
              <CustomPopup
                label="Max days to pay this cash back"
                name="payoutDays"
                required
                value={payoutDays}
                onChange={(e) => setPayoutDays(e.target.value)}
                disabled={submitting}
                placeholder="Select days (0-10)"
                searchable={false}
                options={PAYOUT_DAY_OPTIONS}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label htmlFor="qty" className="mb-2 block text-sm font-semibold text-slate-700">Qty</label>
            <input
              id="qty"
              type="number"
              min="1"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={submitting}
              className={inputBase}
            />
          </div>
          <div className="col-span-2">
            <label htmlFor="reason" className="mb-2 block text-sm font-semibold text-slate-700">Reason</label>
            <input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              placeholder="e.g. Wrong fit, damaged"
              className={inputBase}
            />
          </div>
        </div>

        {formError && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-rose-500">
            <FiAlertCircle className="h-3.5 w-3.5" /> {formError}
          </p>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting} className="gap-2">
            <FiSend className="h-4 w-4" />
            Log return
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ── Returnable sale card (New Return tab) ─────────────────────────────────────
const SaleCard = ({ sale, onPickProduct }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          <FiUser className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-base font-bold text-slate-900">{sale.customer_name || 'Customer'}</h3>
          <p className="mt-0.5 text-sm text-slate-500">{sale.customer_phone || '-'}</p>
          <p className="mt-0.5 text-xs font-medium text-slate-400">{sale.car} · sold {shortDate(sale.sold_at)}</p>
        </div>
      </div>
      <span className="inline-flex shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-600">
        {sale.status}
      </span>
    </div>

    <div className="mt-4 space-y-2">
      {(sale.parts || []).length === 0 ? (
        <p className="text-sm text-slate-400">No itemized products on this sale.</p>
      ) : (
        sale.parts.map((part) => (
          <div
            key={part.inquiry_part_id}
            className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">
                {part.name}
                {part.quantity > 1 && <span className="ml-1 text-xs font-normal text-slate-400">×{part.quantity}</span>}
              </p>
              {part.sold_price != null && (
                <p className="text-xs text-slate-500">
                  {formatPrice(part.sold_price)}
                  {part.source ? ` · ${String(part.source).toUpperCase()}` : ''}
                </p>
              )}
            </div>
            <Button size="sm" variant="secondary" onClick={() => onPickProduct(sale, part)} className="gap-1.5 shrink-0">
              <FiRotateCcw className="h-3.5 w-3.5" />
              Return
            </Button>
          </div>
        ))
      )}
    </div>
  </div>
);

// ── Logged-return card (Returns tab) ──────────────────────────────────────────
const ReturnCard = ({ item, onResolve, resolvingId, onDelete }) => {
  const meta = STATUS_META[item.status] || STATUS_META.pending;
  const isCash = item.return_type === 'cash';
  const busy = resolvingId === item.id;
  // A cash refund already PAID OUT is real money that moved — backend refuses
  // to delete it, so don't offer the button.
  const deletable = !(isCash && item.cash_paid);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isCash ? 'bg-emerald-50 text-emerald-600' : 'bg-violet-50 text-violet-600'}`}>
            {isCash ? <FiDollarSign className="h-5 w-5" /> : <FiRepeat className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 truncate text-base font-bold text-slate-900">
              {item.product_name}
              {item.quantity > 1 && <span className="text-xs font-normal text-slate-400">×{item.quantity}</span>}
              {item.return_kind === 'cancelled' && item.status !== 'rejected' && (
                <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-600">Cancelled</span>
              )}
            </h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {item.customer_name || 'Customer'} · {item.car}
            </p>
            <p className="mt-1 text-xs font-medium text-slate-400">
              {isCash ? `Cash refund ${formatPrice(item.refund_amount) || ''}` : `Exchanged for ${item.exchange_for || '-'}`}
              {!isCash && item.exchange_eta && ` · ${item.exchange_eta}`}
              {isCash && item.cash_payout_days != null && ` · max ${item.cash_payout_days} day${item.cash_payout_days === 1 ? '' : 's'} to pay`}
              {' · '}sold {shortDate(item.sold_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${meta.className}`}>
            {meta.label}
          </span>
          {deletable && (
            <button
              type="button"
              onClick={() => onDelete(item)}
              title="Delete this return record (kept in Deleted Records)"
              className="rounded-lg border border-slate-200 p-1.5 text-slate-300 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
            >
              <FiTrash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {item.reason && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">“{item.reason}”</p>}
      {item.resolution_note && <p className="mt-2 text-xs italic text-slate-500">Resolution: {item.resolution_note}</p>}

      {item.status === 'pending' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {isCash && item.cash_days_left != null && (
            <span className="mr-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
              <FiClock className="h-3.5 w-3.5" />
              Pay within {item.cash_days_left} day{item.cash_days_left === 1 ? '' : 's'}
            </span>
          )}
          {!isCash && item.exchange_eta && (
            <span className="mr-auto inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-700">
              <FiClock className="h-3.5 w-3.5" />
              {item.exchange_eta}
            </span>
          )}
          <Button size="sm" variant="primary" disabled={busy} onClick={() => onResolve(item, 'completed')} className="gap-1.5">
            <FiCheckCircle className="h-3.5 w-3.5" /> Complete
          </Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onResolve(item, 'rejected')} className="gap-1.5">
            <FiSlash className="h-3.5 w-3.5" /> Reject
          </Button>
        </div>
      )}
    </div>
  );
};

const ReturnsPage = () => {
  const [activeTab, setActiveTab] = useState(TABS.NEW);

  const [sales, setSales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);
  const [saleSearch, setSaleSearch] = useState('');
  const [saleFrom, setSaleFrom] = useState('');   // sold-date range
  const [saleTo, setSaleTo] = useState('');

  const [returns, setReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [retFrom, setRetFrom] = useState('');     // return-date range
  const [retTo, setRetTo] = useState('');

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [target, setTarget] = useState(null); // { sale, part }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const loadSales = useCallback(async ({ q = '', from = '', to = '' } = {}) => {
    setLoadingSales(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await returnsApi.getReturnableSales(params);
      setSales(data?.sales || []);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load sold orders'));
    } finally {
      setLoadingSales(false);
    }
  }, []);

  const loadReturns = useCallback(async () => {
    setLoadingReturns(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (retFrom) params.from = retFrom;
      if (retTo) params.to = retTo;
      const { data } = await returnsApi.getReturns(params);
      setReturns(data?.returns || []);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load returns'));
    } finally {
      setLoadingReturns(false);
    }
  }, [statusFilter, typeFilter, retFrom, retTo]);

  // Sold-date changes auto-refresh (search stays on Enter/refresh).
  useEffect(() => { loadSales({ q: saleSearch, from: saleFrom, to: saleTo }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSales, saleFrom, saleTo]);
  useEffect(() => { loadReturns(); }, [loadReturns]);

  const submitReturn = async (payload) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await returnsApi.createReturn(payload);
      setSuccess(`${payload.product_name} return logged.`);
      setTarget(null);
      await Promise.all([loadReturns(), loadSales({ q: saleSearch, from: saleFrom, to: saleTo })]);
    } catch (e) {
      setSubmitError(errorMessage(e, 'Failed to log the return'));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveReturn = async (item, nextStatus) => {
    setResolvingId(item.id);
    setError(null);
    try {
      await returnsApi.updateReturn(item.id, { status: nextStatus });
      setSuccess(`Return marked ${nextStatus}.`);
      await loadReturns();
    } catch (e) {
      setError(errorMessage(e, 'Failed to update the return'));
    } finally {
      setResolvingId(null);
    }
  };

  const submitDelete = async ({ reason }) => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await returnsApi.deleteReturn(deleteTarget.id, { reason });
      setSuccess(`Return for ${deleteTarget.product_name} deleted — saved in Deleted Records.`);
      setDeleteTarget(null);
      await loadReturns();
    } catch (e) {
      setDeleteError(errorMessage(e, 'Failed to delete the return'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const pendingCount = useMemo(() => returns.filter((r) => r.status === 'pending').length, [returns]);

  const STATUS_OPTS = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
  ];
  const TYPE_OPTS = [
    { value: 'all', label: 'All types' },
    { value: 'cash', label: 'Cash' },
    { value: 'exchange', label: 'Exchange' },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        <PageHeader purpose="A customer brought a sold product back — log it against the sale and settle it as an exchange or a cash refund. Both can be done any time; cash refunds then show up in Expenses as a payout to hand over.">
          <Button
            variant="secondary"
            onClick={() => (activeTab === TABS.NEW ? loadSales({ q: saleSearch, from: saleFrom, to: saleTo }) : loadReturns())}
            disabled={loadingSales || loadingReturns}
            className="gap-2"
          >
            <FiRefreshCw className={`h-4 w-4 ${(loadingSales || loadingReturns) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </PageHeader>

        {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

        <div className="rounded-xl bg-slate-200/70 p-1" role="tablist">
          <div className="grid grid-cols-2 gap-1">
            <TabButton active={activeTab === TABS.NEW} icon={FiRotateCcw} label="New Return" onClick={() => setActiveTab(TABS.NEW)} />
            <TabButton active={activeTab === TABS.LOG} icon={FiPackage} label="Returns" count={returns.length} onClick={() => setActiveTab(TABS.LOG)} />
          </div>
        </div>

        {activeTab === TABS.NEW && (
          <div className="space-y-5">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Pick the sold product</h2>
                  <p className="text-sm text-slate-500">Confirmed and completed sales, most recent first.</p>
                </div>
                <div className="relative w-full sm:max-w-sm">
                  <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={saleSearch}
                    onChange={(e) => setSaleSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadSales({ q: saleSearch, from: saleFrom, to: saleTo })}
                    placeholder="Search customer, car, or part…"
                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Sold date</span>
                <DateRange
                  label="Sold date"
                  from={saleFrom}
                  to={saleTo}
                  onFrom={setSaleFrom}
                  onTo={setSaleTo}
                  onClear={() => { setSaleFrom(''); setSaleTo(''); }}
                />
              </div>
            </div>

            {loadingSales ? (
              <Spinner />
            ) : sales.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-4 py-16 text-center">
                <FiPackage className="mb-3 h-8 w-8 text-slate-300" />
                <p className="font-semibold text-slate-700">{saleSearch ? 'No matching sales' : 'No sold orders yet'}</p>
                <p className="mt-1 text-sm text-slate-500">Confirmed and completed sales show up here so you can log returns.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sales.map((sale) => (
                  <SaleCard key={sale.inquiry_id} sale={sale} onPickProduct={(s, p) => { setSubmitError(null); setTarget({ sale: s, part: p }); }} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === TABS.LOG && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard icon={FiPackage} label="Total returns" value={returns.length} tone="violet" />
              <StatCard icon={FiClock} label="Pending" value={pendingCount} tone="amber" />
              <StatCard icon={FiDollarSign} label="Cash refunds" value={returns.filter((r) => r.return_type === 'cash').length} tone="emerald" />
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-1.5">
                  {STATUS_OPTS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setStatusFilter(opt.value)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${statusFilter === opt.value ? 'bg-violet-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <span className="mx-1 h-4 w-px bg-slate-200" />
                <div className="flex flex-wrap gap-1.5">
                  {TYPE_OPTS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setTypeFilter(opt.value)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${typeFilter === opt.value ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Return date</span>
                <DateRange
                  label="Return date"
                  from={retFrom}
                  to={retTo}
                  onFrom={setRetFrom}
                  onTo={setRetTo}
                  onClear={() => { setRetFrom(''); setRetTo(''); }}
                />
              </div>
            </div>

            {loadingReturns ? (
              <Spinner />
            ) : returns.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-4 py-16 text-center">
                <FiRotateCcw className="mb-3 h-8 w-8 text-slate-300" />
                <p className="font-semibold text-slate-700">No returns logged</p>
                <p className="mt-1 text-sm text-slate-500">Returns you log from the New Return tab appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {returns.map((item) => (
                  <ReturnCard
                    key={item.id}
                    item={item}
                    onResolve={resolveReturn}
                    resolvingId={resolvingId}
                    onDelete={(r) => { setDeleteError(''); setDeleteTarget(r); }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {target && (
        <MarkReturnModal
          sale={target.sale}
          part={target.part}
          submitting={submitting}
          error={submitError}
          onClose={() => { if (!submitting) { setTarget(null); setSubmitError(null); } }}
          onSubmit={submitReturn}
        />
      )}

      {deleteTarget && (
        <DeleteConfirm
          title="Delete this return record?"
          summary={`${deleteTarget.customer_name || 'Customer'} · ${deleteTarget.product_name}${deleteTarget.refund_amount ? ` · ${formatPrice(deleteTarget.refund_amount)}` : ''}`}
          consequences={[
            'The return is removed — the part counts as SOLD again in the money math.',
            'Use this only for a return that was logged by mistake.',
          ]}
          busy={deleteBusy}
          error={deleteError}
          onClose={() => { if (!deleteBusy) { setDeleteTarget(null); setDeleteError(''); } }}
          onConfirm={submitDelete}
        />
      )}
    </MainLayout>
  );
};

export default ReturnsPage;
